<template>
  <div class="privacy-score-container">
    <h2>{{ t('privacyScore.title') }}</h2>
    <div class="score-display">
      <div class="score-circle" :style="{ backgroundColor: scoreColor }">
        <div class="score-number">{{ score }}%</div>
      </div>
      <div class="score-summary">
        <h3>{{ scoreTitle }}</h3>
        <p>{{ scoreDescription }}</p>
      </div>
    </div>
    
    <div class="detailed-scores">
      <div class="section-header">
        <h3>{{ t('privacyScore.detailedScores') }}</h3>
      </div>
      
      <div class="category-factors">
        <div v-for="(factor, factorIndex) in ipAnonymityFactors" :key="factorIndex" class="factor-item">
          <div class="factor-name">{{ factor.name }}</div>
          <div class="factor-status" :class="{ 'status-good': factor.impact > 0, 'status-bad': factor.impact < 0 }">
            {{ factor.status }}
            <span class="factor-impact">{{ factor.impact > 0 ? '+' : '' }}{{ factor.impact }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div class="score-recommendations" v-if="recommendations.length > 0">
      <h3>{{ t('privacyScore.recommendations.title') }}</h3>
      <ul>
        <li v-for="(rec, index) in recommendations" :key="index">
          {{ rec }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
  name: 'PrivacyScore',
  props: {
    ipInfo: {
      type: Object,
      default: () => ({})
    },
    browserInfo: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const { t } = useI18n();
    
    // IP匿名性评分因素
    const ipAnonymityFactors = computed(() => {
      return [
        {
          name: t('privacyScore.factors.proxy'),
          status: !props.ipInfo.usingProxy ? t('privacyScore.status.no') : t('privacyScore.status.yes'),
          impact: !props.ipInfo.usingProxy ? 10 : -10,
          recommendation: props.ipInfo.usingProxy ? t('privacyScore.recommendations.nonProxyBenefit') : null
        },
        {
          name: t('privacyScore.factors.dnsLeak'),
          status: props.ipInfo.hasDNSLeak ? t('privacyScore.status.leaked') : t('privacyScore.status.notLeaked'),
          impact: props.ipInfo.hasDNSLeak ? -15 : 15,
          recommendation: props.ipInfo.hasDNSLeak ? t('privacyScore.recommendations.fixDNSLeak') : null
        },
        {
          name: t('privacyScore.factors.ipMatchesCountry'),
          status: checkIpCountryMatch(props.ipInfo) ? t('privacyScore.status.match') : t('privacyScore.status.mismatch'),
          impact: checkIpCountryMatch(props.ipInfo) ? 10 : -10,
          recommendation: checkIpCountryMatch(props.ipInfo) ? null : t('privacyScore.recommendations.ipCountryMismatch')
        },
        {
          name: t('privacyScore.factors.timezone'),
          status: checkTimezoneMatch(props.ipInfo) ? t('privacyScore.status.match') : t('privacyScore.status.mismatch'),
          impact: checkTimezoneMatch(props.ipInfo) ? 5 : -5,
          recommendation: checkTimezoneMatch(props.ipInfo) ? null : t('privacyScore.recommendations.timezoneMismatch')
        },
        {
          name: t('privacyScore.factors.webRTC'),
          status: isWebRTCIPLeaking(props.ipInfo, props.browserInfo) ? t('privacyScore.status.leaked') : t('privacyScore.status.notLeaked'),
          impact: isWebRTCIPLeaking(props.ipInfo, props.browserInfo) ? -5 : 5,
          recommendation: isWebRTCIPLeaking(props.ipInfo, props.browserInfo) ? t('privacyScore.recommendations.disableWebRTC') : null
        },
        {
          name: t('privacyScore.factors.languageMatch'),
          status: checkLanguageMatch(props.ipInfo) ? t('privacyScore.status.match') : t('privacyScore.status.mismatch'),
          impact: checkLanguageMatch(props.ipInfo) ? 5 : -5,
          recommendation: checkLanguageMatch(props.ipInfo) ? null : t('privacyScore.recommendations.languageMismatch')
        },
      ];
    });
    
    // 计算总分 (类似whoer.net)
    const score = computed(() => {
      let positiveImpact = 0;
      let negativeImpact = 0;
      let baseScore = 50;
      
      ipAnonymityFactors.value.forEach(factor => {
        if (factor.impact > 0) {
          positiveImpact += factor.impact;
        } else {
          negativeImpact += factor.impact;
        }
      });
      
      // 根据正面和负面的影响调整分数
      return Math.max(0, Math.min(100, baseScore + positiveImpact + negativeImpact));
    });
    
    // 根据总分确定颜色
    const scoreColor = computed(() => {
      if (score.value >= 80) return '#4CAF50'; // 绿色
      if (score.value >= 60) return '#8BC34A'; // 浅绿色
      if (score.value >= 40) return '#FFC107'; // 黄色
      if (score.value >= 20) return '#FF9800'; // 橙色
      return '#F44336'; // 红色
    });
    
    // 生成基于得分的标题
    const scoreTitle = computed(() => {
      if (score.value >= 80) {
        return t('privacyScore.titles.excellent');
      } else if (score.value >= 60) {
        return t('privacyScore.titles.good');
      } else if (score.value >= 40) {
        return t('privacyScore.titles.fair');
      } else if (score.value >= 20) {
        return t('privacyScore.titles.poor');
      } else {
        return t('privacyScore.titles.critical');
      }
    });
    
    // 生成基于得分的描述
    const scoreDescription = computed(() => {
      if (score.value >= 80) {
        return t('privacyScore.descriptions.excellent');
      } else if (score.value >= 60) {
        return t('privacyScore.descriptions.good');
      } else if (score.value >= 40) {
        return t('privacyScore.descriptions.fair');
      } else if (score.value >= 20) {
        return t('privacyScore.descriptions.poor');
      } else {
        return t('privacyScore.descriptions.critical');
      }
    });
    
    // 获取推荐列表
    const recommendations = computed(() => {
      // 过滤出带有建议的因素
      return ipAnonymityFactors.value
        .filter(factor => factor.recommendation)
        .map(factor => factor.recommendation);
    });
    
    // 辅助函数: 检查IP与国家是否匹配
    function checkIpCountryMatch(ipInfo) {
      if (!ipInfo || !ipInfo.country) return true;
      
      // 尝试从浏览器语言获取国家代码
      const browserLang = navigator.language || navigator.userLanguage;
      let userCountryFromLang = null;
      
      if (browserLang && browserLang.includes('-')) {
        userCountryFromLang = browserLang.split('-')[1].toUpperCase();
      }
      
      // 尝试从时区获取国家信息
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let userCountryFromTimezone = null;
      
      // 时区到国家的映射
      const timezoneToCountry = {
        // 北美洲
        'America/New_York': 'US',
        'America/Los_Angeles': 'US',
        'America/Chicago': 'US',
        'America/Denver': 'US',
        'America/Phoenix': 'US',
        'America/Anchorage': 'US',
        'America/Juneau': 'US',
        'America/Honolulu': 'US',
        'America/Adak': 'US',
        'America/Detroit': 'US',
        'America/Indianapolis': 'US',
        'America/Toronto': 'CA',
        'America/Vancouver': 'CA',
        'America/Montreal': 'CA',
        'America/Edmonton': 'CA',
        'America/Calgary': 'CA',
        'America/Winnipeg': 'CA',
        'America/Halifax': 'CA',
        'America/St_Johns': 'CA',
        'America/Mexico_City': 'MX',
        'America/Tijuana': 'MX',
        'America/Monterrey': 'MX',
        'America/Merida': 'MX',
        'America/Cancun': 'MX',
        
        // 中美洲和加勒比地区
        'America/Panama': 'PA',
        'America/Costa_Rica': 'CR',
        'America/El_Salvador': 'SV',
        'America/Guatemala': 'GT',
        'America/Tegucigalpa': 'HN',
        'America/Managua': 'NI',
        'America/Jamaica': 'JM',
        'America/Puerto_Rico': 'PR',
        'America/Santo_Domingo': 'DO',
        'America/Havana': 'CU',
        'America/Port-au-Prince': 'HT',
        
        // 南美洲
        'America/Sao_Paulo': 'BR',
        'America/Rio_Branco': 'BR',
        'America/Manaus': 'BR',
        'America/Belem': 'BR',
        'America/Fortaleza': 'BR',
        'America/Recife': 'BR',
        'America/Buenos_Aires': 'AR',
        'America/Cordoba': 'AR',
        'America/Mendoza': 'AR',
        'America/Santiago': 'CL',
        'America/Lima': 'PE',
        'America/Bogota': 'CO',
        'America/Caracas': 'VE',
        'America/Guayaquil': 'EC',
        'America/La_Paz': 'BO',
        'America/Asuncion': 'PY',
        'America/Montevideo': 'UY',
        'America/Paramaribo': 'SR',
        'America/Cayenne': 'GF',
        
        // 欧洲
        'Europe/London': 'GB',
        'Europe/Dublin': 'IE',
        'Europe/Paris': 'FR',
        'Europe/Berlin': 'DE',
        'Europe/Madrid': 'ES',
        'Europe/Barcelona': 'ES',
        'Europe/Rome': 'IT',
        'Europe/Amsterdam': 'NL',
        'Europe/Brussels': 'BE',
        'Europe/Vienna': 'AT',
        'Europe/Moscow': 'RU',
        'Europe/Kaliningrad': 'RU',
        'Europe/Samara': 'RU',
        'Europe/Volgograd': 'RU',
        'Europe/Stockholm': 'SE',
        'Europe/Oslo': 'NO',
        'Europe/Copenhagen': 'DK',
        'Europe/Helsinki': 'FI',
        'Europe/Warsaw': 'PL',
        'Europe/Prague': 'CZ',
        'Europe/Budapest': 'HU',
        'Europe/Sofia': 'BG',
        'Europe/Bucharest': 'RO',
        'Europe/Istanbul': 'TR',
        'Europe/Athens': 'GR',
        'Europe/Zurich': 'CH',
        'Europe/Luxembourg': 'LU',
        'Europe/Lisbon': 'PT',
        'Europe/Belgrade': 'RS',
        'Europe/Zagreb': 'HR',
        'Europe/Sarajevo': 'BA',
        'Europe/Skopje': 'MK',
        'Europe/Chisinau': 'MD',
        'Europe/Kiev': 'UA',
        'Europe/Minsk': 'BY',
        'Europe/Riga': 'LV',
        'Europe/Tallinn': 'EE',
        'Europe/Vilnius': 'LT',
        'Europe/Malta': 'MT',
        'Europe/Bratislava': 'SK',
        'Europe/Ljubljana': 'SI',
        
        // 亚洲
        'Asia/Tokyo': 'JP',
        'Asia/Osaka': 'JP',
        'Asia/Sapporo': 'JP',
        'Asia/Shanghai': 'CN',
        'Asia/Chongqing': 'CN',
        'Asia/Harbin': 'CN',
        'Asia/Urumqi': 'CN',
        'Asia/Hong_Kong': 'HK',
        'Asia/Macau': 'MO',
        'Asia/Taipei': 'TW',
        'Asia/Seoul': 'KR',
        'Asia/Pyongyang': 'KP',
        'Asia/Singapore': 'SG',
        'Asia/Kuala_Lumpur': 'MY',
        'Asia/Bangkok': 'TH',
        'Asia/Jakarta': 'ID',
        'Asia/Makassar': 'ID',
        'Asia/Jayapura': 'ID',
        'Asia/Manila': 'PH',
        'Asia/Ho_Chi_Minh': 'VN',
        'Asia/Hanoi': 'VN',
        'Asia/Phnom_Penh': 'KH',
        'Asia/Vientiane': 'LA',
        'Asia/Yangon': 'MM',
        'Asia/Dubai': 'AE',
        'Asia/Abu_Dhabi': 'AE',
        'Asia/Muscat': 'OM',
        'Asia/Bahrain': 'BH',
        'Asia/Qatar': 'QA',
        'Asia/Kuwait': 'KW',
        'Asia/Riyadh': 'SA',
        'Asia/Jeddah': 'SA',
        'Asia/Baghdad': 'IQ',
        'Asia/Tehran': 'IR',
        'Asia/Kabul': 'AF',
        'Asia/Karachi': 'PK',
        'Asia/Lahore': 'PK',
        'Asia/Kolkata': 'IN',
        'Asia/Mumbai': 'IN',
        'Asia/Chennai': 'IN',
        'Asia/Colombo': 'LK',
        'Asia/Dhaka': 'BD',
        'Asia/Kathmandu': 'NP',
        'Asia/Thimphu': 'BT',
        'Asia/Almaty': 'KZ',
        'Asia/Bishkek': 'KG',
        'Asia/Tashkent': 'UZ',
        'Asia/Dushanbe': 'TJ',
        'Asia/Ashgabat': 'TM',
        'Asia/Yerevan': 'AM',
        'Asia/Baku': 'AZ',
        'Asia/Tbilisi': 'GE',
        'Asia/Tel_Aviv': 'IL',
        'Asia/Jerusalem': 'IL',
        'Asia/Amman': 'JO',
        'Asia/Beirut': 'LB',
        'Asia/Damascus': 'SY',
        
        // 大洋洲
        'Australia/Sydney': 'AU',
        'Australia/Melbourne': 'AU',
        'Australia/Brisbane': 'AU',
        'Australia/Adelaide': 'AU',
        'Australia/Perth': 'AU',
        'Australia/Darwin': 'AU',
        'Australia/Hobart': 'AU',
        'Australia/Canberra': 'AU',
        'Pacific/Auckland': 'NZ',
        'Pacific/Christchurch': 'NZ',
        'Pacific/Wellington': 'NZ',
        'Pacific/Fiji': 'FJ',
        'Pacific/Port_Moresby': 'PG',
        'Pacific/Guadalcanal': 'SB',
        'Pacific/Noumea': 'NC',
        'Pacific/Pago_Pago': 'AS',
        'Pacific/Honolulu': 'US',
        'Pacific/Tahiti': 'PF',
        'Pacific/Guam': 'GU',
        'Pacific/Saipan': 'MP',
        'Pacific/Apia': 'WS',
        'Pacific/Tongatapu': 'TO',
        
        // 非洲
        'Africa/Cairo': 'EG',
        'Africa/Johannesburg': 'ZA',
        'Africa/Lagos': 'NG',
        'Africa/Nairobi': 'KE',
        'Africa/Casablanca': 'MA',
        'Africa/Tunis': 'TN',
        'Africa/Algiers': 'DZ',
        'Africa/Tripoli': 'LY',
        'Africa/Khartoum': 'SD',
        'Africa/Addis_Ababa': 'ET',
        'Africa/Dar_es_Salaam': 'TZ',
        'Africa/Kampala': 'UG',
        'Africa/Mogadishu': 'SO',
        'Africa/Lusaka': 'ZM',
        'Africa/Harare': 'ZW',
        'Africa/Maputo': 'MZ',
        'Africa/Luanda': 'AO',
        'Africa/Kinshasa': 'CD',
        'Africa/Lubumbashi': 'CD',
        'Africa/Brazzaville': 'CG',
        'Africa/Dakar': 'SN',
        'Africa/Accra': 'GH',
        'Africa/Abidjan': 'CI',
        'Africa/Nouakchott': 'MR',
        'Africa/Windhoek': 'NA',
        'Africa/Gaborone': 'BW',
        'Africa/Bujumbura': 'BI',
        'Africa/Kigali': 'RW',
        
        // 印度洋
        'Indian/Maldives': 'MV',
        'Indian/Mauritius': 'MU',
        'Indian/Reunion': 'RE',
        'Indian/Mahe': 'SC',
        'Indian/Antananarivo': 'MG',
        'Indian/Comoro': 'KM',
        'Indian/Mayotte': 'YT',
        'Indian/Kerguelen': 'TF',
        'Indian/Chagos': 'IO'
      };
      
      userCountryFromTimezone = timezoneToCountry[timezone];
      
      // 获取IP国家代码
      let ipCountryCode = ipInfo.countryCode || '';
      
      // 如果没有IP国家代码，尝试从国家名称映射（仅包含一些常见国家）
      if (!ipCountryCode && ipInfo.country) {
        const countryToCode = {
          'United States': 'US',
          'United Kingdom': 'GB',
          'China': 'CN',
          'Japan': 'JP',
          'Germany': 'DE',
          'France': 'FR',
          'Russia': 'RU',
          'Canada': 'CA',
          'Australia': 'AU',
          'Brazil': 'BR',
          'India': 'IN'
          // 可以根据需要扩展更多的映射
        };
        
        ipCountryCode = countryToCode[ipInfo.country] || '';
      }
      
      // 检查IP国家与用户国家是否匹配
      if (userCountryFromLang && ipCountryCode) {
        return userCountryFromLang === ipCountryCode;
      }
      
      if (userCountryFromTimezone && ipCountryCode) {
        return userCountryFromTimezone === ipCountryCode;
      }
      
      // 如果无法确定，默认为匹配
      return true;
    }
    
    // 辅助函数: 检查时区是否匹配地理位置
    function checkTimezoneMatch(ipInfo) {
      
      // 如果没有IP时区信息，无法进行比较
      if (!ipInfo || !ipInfo.timezone) {
        return true; // 默认匹配
      }
      
      // 获取浏览器/系统时区
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // console.log(browserTimezone);
      // 直接比较时区是否完全匹配
      if (browserTimezone === ipInfo.timezone) {
        return true;
      }
      // 如果没有完全匹配，检查时区偏移是否相同
      try {
        // 获取当前日期
        const now = new Date();
        
        // 计算IP时区的偏移量（分钟）
        const ipOffset = getTimezoneOffset(ipInfo.timezone, now);
        
        // 计算浏览器时区的偏移量（分钟）
        const browserOffset = getTimezoneOffset(browserTimezone, now);
        // 比较偏移量是否相同（考虑1小时内的误差）
        return Math.abs(ipOffset - browserOffset) < 50;
      } catch (error) {
        console.error('比较时区时出错:', error);
        return true; // 出错时默认匹配
      }
    }
    
    // 辅助函数: 获取指定时区在指定日期的偏移量（单位：分钟）
    function getTimezoneOffset(timezone, date) {
      const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
      const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
      const offset = (tzDate.getTime() - utcDate.getTime()) / 60000; // 转换为分钟
      return offset;
    }
    
    // 辅助函数: 检查WebRTC IP是否泄露（WebRTC公网IP与本机IP不一致）
    function isWebRTCIPLeaking(ipInfo, browserInfo) {
      // 如果没有WebRTC或浏览器不支持WebRTC，则没有泄漏
      if (!browserInfo.webRTCEnabled || !browserInfo.webRTCIPs) {
        return false;
      }
      
      // 检查WebRTC公网IP是否与本机IP一致
      const machineIP = ipInfo.ip;
      
      // 如果WebRTC IPs包含任何与本机IP不同的公网IP，则认为有泄漏
      if (Array.isArray(browserInfo.webRTCIPs)) {
        // 过滤出非本地IP地址（公网IP）
        const publicIPs = browserInfo.webRTCIPs.filter(ip => 
          !ip.startsWith('192.168.') && 
          !ip.startsWith('10.') && 
          !ip.startsWith('172.') &&
          !ip.match(/^127\.\d+\.\d+\.\d+$/) &&
          !ip.match(/^::1$/) &&
          !ip.match(/^[fF][cCdD]/)
        );
        
        // 检查是否有任何公网IP与本机IP不同
        return publicIPs.some(ip => ip !== machineIP);
      }
      
      return false;
    }
    
    // 添加检查语言匹配的辅助函数
    function checkLanguageMatch(ipInfo) {
      if (!ipInfo || !ipInfo.country) return true;
      
      // 获取浏览器语言
      const browserLang = navigator.language || navigator.userLanguage || '';
      const browserLangCode = browserLang.split('-')[0].toLowerCase();
      
      // 国家到主要使用语言的映射
      const countryToLanguages = {
        'US': ['en'],
        'GB': ['en'],
        'CA': ['en', 'fr'],
        'AU': ['en'],
        'NZ': ['en'],
        'CN': ['zh'],
        'TW': ['zh'],
        'HK': ['zh', 'en'],
        'JP': ['ja'],
        'KR': ['ko'],
        'DE': ['de'],
        'AT': ['de'],
        'CH': ['de', 'fr', 'it'],
        'FR': ['fr'],
        'ES': ['es'],
        'MX': ['es'],
        'IT': ['it'],
        'PT': ['pt'],
        'BR': ['pt'],
        'RU': ['ru'],
        'NL': ['nl'],
        'BE': ['nl', 'fr'],
        'SE': ['sv'],
        'NO': ['no'],
        'DK': ['da'],
        'FI': ['fi'],
        'PL': ['pl'],
        'CZ': ['cs'],
        'TR': ['tr'],
        'GR': ['el'],
        'IL': ['he'],
        'AE': ['ar'],
        'SA': ['ar'],
        'IN': ['hi', 'en'],
        'TH': ['th'],
        'VN': ['vi'],
        'ID': ['id']
      };
      
      // 获取IP国家代码
      let ipCountryCode = ipInfo.countryCode || '';
      
      // 如果没有IP国家代码，尝试从国家名称映射
      if (!ipCountryCode && ipInfo.country) {
        const countryToCode = {
          'United States': 'US',
          'United Kingdom': 'GB',
          'China': 'CN',
          'Japan': 'JP',
          'Germany': 'DE',
          'France': 'FR',
          'Russia': 'RU',
          'Canada': 'CA',
          'Australia': 'AU',
          'Brazil': 'BR',
          'India': 'IN',
          'Spain': 'ES',
          'Italy': 'IT',
          'Mexico': 'MX',
          'South Korea': 'KR',
          'Netherlands': 'NL',
          'Switzerland': 'CH'
        };
        
        ipCountryCode = countryToCode[ipInfo.country] || '';
      }
      
      // 检查浏览器语言是否在IP国家的常用语言列表中
      if (ipCountryCode && countryToLanguages[ipCountryCode]) {
        return countryToLanguages[ipCountryCode].includes(browserLangCode);
      }
      
      // 无法确定匹配关系时，默认为匹配
      return true;
    }
    
    return {
      t,
      score,
      scoreColor,
      scoreTitle,
      scoreDescription,
      ipAnonymityFactors,
      recommendations
    };
  }
}
</script>

<style scoped>
.privacy-score-container {
  /* padding: 25px;
  background-color: #f8f9fa; */
  /* border-radius: 12px; */
  margin-bottom: 20px;
  /* box-shadow: 0 2px 10px rgba(0,0,0,0.05); */
}

.score-display {
  display: flex;
  align-items: center;
  margin: 20px 0;
}

.score-circle {
  width: 100%;
  max-width: 120px;
  aspect-ratio: 1/1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 30px;
  transition: background-color 0.5s ease;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.score-number {
  color: white;
  font-size: 36px;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.score-summary {
  flex-grow: 1;
}

.score-summary h3 {
  font-size: 22px;
  margin-bottom: 10px;
  color: #333;
}

.score-summary p {
  font-size: 16px;
  color: #555;
  line-height: 1.5;
}

.detailed-scores {
  margin-top: 30px;
  background-color: white;
  border-radius: 10px;
  padding: 5px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #eee;
}

.section-header h3 {
  font-size: 18px;
  color: #333;
  margin: 0;
}

.category-factors {
  padding: 10px 15px;
  background-color: white;
  border-radius: 8px;
  margin-top: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.factor-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 10px;
  border-bottom: 1px solid #eee;
}

.factor-item:last-child {
  border-bottom: none;
}

.factor-name {
  color: #555;
  font-size: 14px;
}

.factor-status {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.status-good {
  color: #4CAF50;
}

.status-bad {
  color: #F44336;
}

.factor-impact {
  margin-left: 8px;
  font-weight: bold;
  border-radius: 4px;
  padding: 2px 6px;
  background-color: rgba(0,0,0,0.05);
}

.score-recommendations {
  margin-top: 30px;
  padding: 15px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

.score-recommendations h3 {
  color: #333;
  font-size: 18px;
  margin-bottom: 15px;
}

.score-recommendations ul {
  padding-left: 20px;
}

.score-recommendations li {
  margin-bottom: 8px;
  color: #555;
  line-height: 1.5;
}
</style>
